import React from 'react';
import { motion } from 'motion/react';
import { Activity, Clock, CheckCircle, AlertCircle, Info } from 'lucide-react';

interface ActivityItem {
  id: string;
  action: string;
  time: string;
  status: 'completed' | 'success' | 'warning' | 'info';
  description?: string;
}

const RecentActivityCard: React.FC = () => {
  const activities: ActivityItem[] = [
    {
      id: '1',
      action: 'Profile Updated',
      time: '2 hours ago',
      status: 'completed',
      description: 'Successfully updated profile information',
    },
    {
      id: '2',
      action: 'System Login',
      time: '1 day ago',
      status: 'info',
      description: 'Logged in from new location',
    },
    {
      id: '3',
      action: 'Energy Report Generated',
      time: '2 days ago',
      status: 'success',
      description: 'Monthly energy consumption report',
    },
    {
      id: '4',
      action: 'Security Alert',
      time: '3 days ago',
      status: 'warning',
      description: 'Unusual login attempt detected',
    },
    {
      id: '5',
      action: 'New Organization Created',
      time: '1 week ago',
      status: 'success',
      description: 'Successfully created Enexan Energy Solutions',
    },
  ];

  const getStatusConfig = (status: ActivityItem['status']) => {
    switch (status) {
      case 'completed':
        return {
          color: 'bg-blue-500',
          icon: CheckCircle,
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
        };
      case 'success':
        return {
          color: 'bg-emerald-500',
          icon: CheckCircle,
          bgColor: 'bg-emerald-50',
          textColor: 'text-emerald-700',
        };
      case 'warning':
        return {
          color: 'bg-amber-500',
          icon: AlertCircle,
          bgColor: 'bg-amber-50',
          textColor: 'text-amber-700',
        };
      case 'info':
        return {
          color: 'bg-gray-500',
          icon: Info,
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-700',
        };
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="rounded-3xl border border-gray-100/50 bg-white/80 p-6 shadow-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl"
    >
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
            <p className="text-sm text-gray-600">Your latest account activities</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 rounded-xl bg-gray-100/80 px-4 py-2 text-sm font-medium text-gray-700 backdrop-blur-sm transition-all hover:bg-gray-200/80"
        >
          <Clock className="h-4 w-4" />
          View All
        </motion.button>
      </div>

      {/* Activity Timeline */}
      <div className="space-y-4">
        {activities.map((activity, index) => {
          const config = getStatusConfig(activity.status);
          const Icon = config.icon;

          return (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
              className="group relative flex items-start gap-4 rounded-2xl border border-gray-100/50 bg-gray-50/30 p-4 transition-all duration-200 hover:bg-gray-50/60"
            >
              {/* Timeline Line */}
              {index < activities.length - 1 && (
                <div className="absolute top-12 left-[18px] h-8 w-px bg-gray-200"></div>
              )}

              {/* Status Indicator */}
              <div
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${config.color} shadow-sm`}
              >
                <div className="h-3 w-3 rounded-full bg-white"></div>
              </div>

              {/* Activity Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{activity.action}</p>
                    {activity.description && <p className="mt-1 text-sm text-gray-600">{activity.description}</p>}
                  </div>

                  <div className="flex items-center gap-2">
                    <div className={`rounded-lg px-2 py-1 ${config.bgColor}`}>
                      <Icon className={`h-3 w-3 ${config.textColor}`} />
                    </div>
                    <span className="text-xs font-medium whitespace-nowrap text-gray-500">{activity.time}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Activity Summary */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-900">24</div>
          <div className="text-xs text-gray-600">This Month</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-600">18</div>
          <div className="text-xs text-gray-600">Successful</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600">6</div>
          <div className="text-xs text-gray-600">Updates</div>
        </div>
      </div>
    </motion.div>
  );
};

export default RecentActivityCard;
